/**
 * Centralized Animation System with Motion Integration
 * Consolidates all scroll animations from across the theme
 * Uses Motion when available, falls back to Intersection Observer
 */

// Check if Motion is available - try different possible global names
let hasMotion = false;
let motionLib = null;

try {
  hasMotion = (typeof motion !== 'undefined') || (typeof Motion !== 'undefined') || (typeof window.motion !== 'undefined');
  motionLib = (typeof motion !== 'undefined' ? motion : undefined) || 
              (typeof Motion !== 'undefined' ? Motion : undefined) || 
              (typeof window.motion !== 'undefined' ? window.motion : undefined);
} catch (e) {
  console.log('🎬 Error checking Motion.js:', e);
}

// Motion.js detection complete

// Animation configuration
const AnimationConfig = {
  sections: {
    threshold: 0.1,
    rootMargin: '50px 0px -50px 0px',
    duration: 0.6,
    easing: 'ease-out',
    delay: 0.1
  },
  products: {
    threshold: 0.01,
    rootMargin: '600px 0px 0px 0px',
    duration: 0.4,
    easing: 'ease-out',
    delay: 0,
    staggerDelay: 0.02
  },
  meters: {
    threshold: 0.1,
    rootMargin: '-100px',
    duration: 1.5,
    easing: 'ease-out',
    staggerDelay: 0.2
  },
  lazyLoad: {
    threshold: 0.1,
    rootMargin: '50px 0px -50px 0px',
  }
};

// Centralized Animation Manager
window.AnimationManager = {
  init: function() {
    console.log('🎬 Initializing centralized animation system...');
    
    this.initSectionAnimations();
    this.initProductAnimations();
    this.initImageGridAnimations();
    this.initMegaMenuAnimations();
    this.initHeaderDropdownAnimations();
    this.initMeterAnimations();
    this.initLazyLoading();
    this.initParallaxEffects();
    this.initResponsiveIframes();
    this.initImageLoading();
    this.initProductMedia();
    this.initBreakpointContainerHoverEffects();
    this.initCardListAnimations();
    this.initArticleListAnimations();
    this.initGenericEntranceAnimations();
    
    console.log('✅ All animations initialized');
  },

  // Section fade-in animations (consolidates section-animation.liquid)
  // First section on the page is never animated (visible immediately)
  initSectionAnimations: function() {
    const sections = document.querySelectorAll('#MainContent section:not([data-no-animate]):not(.product-section):not(.collection-grid-section):not(.featured-products):not(.product-recommendations):not(.search-results)');
    
    if (hasMotion) {
      sections.forEach((section, index) => {
        if (index === 0) return;
        section.classList.add('animate-section');
        section.style.opacity = '0';
        section.style.transform = 'translateY(80px)';
        
        motionLib.inView(section, ({ target }) => {
          motionLib.animate(target, 
            { 
              opacity: [0, 1], 
              y: [80, 0] 
            }, 
            { 
              duration: AnimationConfig.sections.duration,
              easing: AnimationConfig.sections.easing,
              delay: AnimationConfig.sections.delay
            }
          );
        }, {
          threshold: AnimationConfig.sections.threshold,
          rootMargin: AnimationConfig.sections.rootMargin,
          once: true
        });
      });
    } else {
      console.log('🎬 Motion.js not available, using Intersection Observer fallback...');
      // Fallback to Intersection Observer
      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            console.log('🎬 Section entered viewport (fallback), animating:', entry.target);
            // Animate using CSS transitions
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
            entry.target.classList.add('animated');
          }
        });
      }, {
        threshold: AnimationConfig.sections.threshold,
        rootMargin: AnimationConfig.sections.rootMargin
      });

      sections.forEach((section, index) => {
        if (index === 0) return;
        section.classList.add('animate-section');
        section.style.opacity = '0';
        section.style.transform = 'translateY(80px)';
        section.style.transition = `opacity ${AnimationConfig.sections.duration}s ${AnimationConfig.sections.easing}, transform ${AnimationConfig.sections.duration}s ${AnimationConfig.sections.easing}`;
        sectionObserver.observe(section);
      });
    }
  },

  // Product card entrance animations
  initProductAnimations: function() {
    // Wait a bit for Swiper to initialize, then target product cards
    setTimeout(() => {
      // Skip animations if Swiper is present - Swiper handles its own animations
      const hasSwiper = document.querySelector('.swiper-wrapper, .swiper-container, .collection-swiper-container');
      if (hasSwiper) {
        console.log('🎬 Swiper detected - skipping product card entrance animations');
        return;
      }
      
      const productCards = document.querySelectorAll('#MainContent .card-wrapper, #MainContent .collection-product-card, #MainContent .grid__item');
      
      if (hasMotion) {
      productCards.forEach((card, index) => {
        card.classList.add('animate-product-card');
        
        // Set initial state
        card.style.opacity = '0';
        card.style.transform = 'translateY(8px) scale(0.995)';
        
        motionLib.inView(card, ({ target }) => {
          motionLib.animate(target, 
            { 
              opacity: [0, 1], 
              y: [8, 0],
              scale: [0.995, 1]
            }, 
            { 
              duration: AnimationConfig.products.duration,
              easing: AnimationConfig.products.easing,
              delay: AnimationConfig.products.delay + (index * AnimationConfig.products.staggerDelay)
            }
          );
        }, {
          threshold: AnimationConfig.products.threshold,
          rootMargin: AnimationConfig.products.rootMargin,
          once: true
        });
      });
    } else {
      // Fallback to Intersection Observer
      const productObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.style.opacity = '1';
              entry.target.style.transform = 'translateY(0) scale(1)';
              entry.target.classList.add('animated');
            }, index * 50);
            productObserver.unobserve(entry.target);
          }
        });
      }, {
        threshold: AnimationConfig.products.threshold,
        rootMargin: AnimationConfig.products.rootMargin
      });

      productCards.forEach(card => {
        card.classList.add('animate-product-card');
        // Set initial state for fallback
        card.style.opacity = '0';
        card.style.transform = 'translateY(8px) scale(0.995)';
        card.style.transition = `opacity ${AnimationConfig.products.duration}s ${AnimationConfig.products.easing}, transform ${AnimationConfig.products.duration}s ${AnimationConfig.products.easing}`;
        productObserver.observe(card);
      });
    }
    }, 100); // Wait 100ms for Swiper to initialize
  },

  // Image grid entrance animations and hover effects
  initImageGridAnimations: function() {
    const imageGridItems = document.querySelectorAll('#MainContent .collection-grid__list li, #MainContent .collection-grid__image-block');
    
    if (hasMotion) {
      imageGridItems.forEach((item, index) => {
        item.classList.add('animate-image-grid-item');
        
        // Set initial state
        item.style.opacity = '0';
        item.style.transform = 'translateY(8px) scale(0.995)';
        
        // Entrance animation
        motionLib.inView(item, ({ target }) => {
          motionLib.animate(target, 
            { 
              opacity: [0, 1], 
              y: [8, 0],
              scale: [0.995, 1]
            }, 
            { 
              duration: AnimationConfig.products.duration,
              easing: AnimationConfig.products.easing,
              delay: index * AnimationConfig.products.staggerDelay
            }
          );
          
          // Animate title text
          const title = item.querySelector('.collection-grid__title');
          if (title) {
            motionLib.animate(title, 
              { 
                opacity: [0, 1] 
              }, 
              { 
                duration: AnimationConfig.products.duration,
                easing: AnimationConfig.products.easing,
                delay: index * AnimationConfig.products.staggerDelay
              }
            );
          }
        }, {
          threshold: AnimationConfig.products.threshold,
          rootMargin: AnimationConfig.products.rootMargin,
          once: true
        });

        // Hover animation for images
        const image = item.querySelector('img, svg');
        if (image) {
          item.addEventListener('mouseenter', function() {
            motionLib.animate(image, 
              { 
                scale: [1, 1.12] 
              },
              { 
                duration: 0.3, 
                easing: 'ease-out' 
              }
            );
          });
          
          item.addEventListener('mouseleave', function() {
            motionLib.animate(image, 
              { 
                scale: [1.12, 1] 
              },
              { 
                duration: 0.3, 
                easing: 'ease-in' 
              }
            );
          });
        }
      });
    } else {
      // Fallback to Intersection Observer
      const imageGridObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.style.opacity = '1';
              entry.target.style.transform = 'translateY(0) scale(1)';
              entry.target.classList.add('animated');
              
              // Animate title text
              const title = entry.target.querySelector('.collection-grid__title');
              if (title) {
                title.style.opacity = '1';
              }
            }, index * 50);
            imageGridObserver.unobserve(entry.target);
          }
        });
      }, {
        threshold: AnimationConfig.products.threshold,
        rootMargin: AnimationConfig.products.rootMargin
      });

      imageGridItems.forEach(item => {
        item.classList.add('animate-image-grid-item');
        // Set initial state for fallback
        item.style.opacity = '0';
        item.style.transform = 'translateY(8px) scale(0.995)';
        item.style.transition = `opacity ${AnimationConfig.products.duration}s ${AnimationConfig.products.easing}, transform ${AnimationConfig.products.duration}s ${AnimationConfig.products.easing}`;
        
        // Fallback hover animation
        const image = item.querySelector('img, svg');
        if (image) {
          image.style.transition = 'transform 0.3s ease';
          item.addEventListener('mouseenter', function() {
            image.style.transform = 'scale(1.12)';
          });
          item.addEventListener('mouseleave', function() {
            image.style.transform = 'scale(1)';
          });
        }
        
        imageGridObserver.observe(item);
      });
    }
  },

  // Mega menu slide-in and staggered animations
  initMegaMenuAnimations: function() {
    const megaMenus = document.querySelectorAll('.header-mega-menu');
    
    if (hasMotion) {
      megaMenus.forEach(menu => {
        // Set initial state for the mega menu container
        menu.style.opacity = '0';
        menu.style.transform = 'translateY(-20px)';
        
        // Add event listeners for hover states
        const menuTrigger = menu.previousElementSibling;
        if (menuTrigger) {
          let isMenuVisible = false;
          let hideTimeout = null;
          
          const showMenu = () => {
            if (hideTimeout) {
              clearTimeout(hideTimeout);
              hideTimeout = null;
            }
            
            if (isMenuVisible) return;
            isMenuVisible = true;
            
            // Slide in the mega menu
            motionLib.animate(menu, 
              { 
                opacity: [0, 1], 
                y: [-20, 0] 
              }, 
              { 
                duration: 0.3,
                easing: 'ease-out'
              }
            );
            
            // Stagger fadeInDown for direct children
            const directChildren = menu.querySelectorAll('.header-mega-menu__wrapper > *, .header-mega-menu__collection-list > *, .mega-menu-custom > *');
            directChildren.forEach((child, index) => {
              child.style.opacity = '0';
              child.style.transform = 'translateY(-20px)';
              
              motionLib.animate(child, 
                { 
                  opacity: [0, 1], 
                  y: [-20, 0] 
                }, 
                { 
                  duration: 0.4,
                  easing: 'ease-out',
                  delay: index * 0.1
                }
              );
            });
            
            // Add hover zoom effects to mega menu images
            this.addMegaMenuHoverEffects(menu);
          };
          
          const hideMenu = () => {
            if (!isMenuVisible) return;
            isMenuVisible = false;
            
            // Fade out the mega menu
            motionLib.animate(menu, 
              { 
                opacity: [1, 0], 
                y: [0, -20] 
              }, 
              { 
                duration: 0.2,
                easing: 'ease-in',
                onComplete: () => {
                  // Reset children only after menu is completely hidden
                  const directChildren = menu.querySelectorAll('.header-mega-menu__wrapper > *, .header-mega-menu__collection-list > *, .mega-menu-custom > *');
                  directChildren.forEach(child => {
                    child.style.opacity = '0';
                    child.style.transform = 'translateY(-20px)';
                  });
                }
              }
            );
          };
          
          // Show menu on trigger hover
          menuTrigger.addEventListener('mouseenter', showMenu);
          
          // Hide menu with delay on trigger leave
          menuTrigger.addEventListener('mouseleave', () => {
            hideTimeout = setTimeout(hideMenu, 100);
          });
          
          // Keep menu visible when hovering over it
          menu.addEventListener('mouseenter', () => {
            if (hideTimeout) {
              clearTimeout(hideTimeout);
              hideTimeout = null;
            }
          });
          
          // Hide menu when leaving the menu itself
          menu.addEventListener('mouseleave', () => {
            hideTimeout = setTimeout(hideMenu, 100);
          });
        }
      });
    } else {
      // Fallback to CSS transitions
      megaMenus.forEach(menu => {
        menu.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out';
        menu.style.opacity = '0';
        menu.style.transform = 'translateY(-20px)';
        
        const menuTrigger = menu.previousElementSibling;
        if (menuTrigger) {
          let isMenuVisible = false;
          let hideTimeout = null;
          
          const showMenu = () => {
            if (hideTimeout) {
              clearTimeout(hideTimeout);
              hideTimeout = null;
            }
            
            if (isMenuVisible) return;
            isMenuVisible = true;
            
            menu.style.opacity = '1';
            menu.style.transform = 'translateY(0)';
            
            // Stagger children with CSS
            const directChildren = menu.querySelectorAll('.header-mega-menu__wrapper > *, .header-mega-menu__collection-list > *, .mega-menu-custom > *');
            directChildren.forEach((child, index) => {
              child.style.transition = 'opacity 0.4s ease-out, transform 0.4s ease-out';
              child.style.transitionDelay = `${index * 0.1}s`;
              child.style.opacity = '0';
              child.style.transform = 'translateY(-20px)';
              
              setTimeout(() => {
                child.style.opacity = '1';
                child.style.transform = 'translateY(0)';
              }, 50);
            });
            
            // Add hover zoom effects to mega menu images (CSS fallback)
            this.addMegaMenuHoverEffectsCSS(menu);
          };
          
          const hideMenu = () => {
            if (!isMenuVisible) return;
            isMenuVisible = false;
            
            menu.style.opacity = '0';
            menu.style.transform = 'translateY(-20px)';
            
            // Reset children after a delay to allow menu to fade out first
            setTimeout(() => {
              const directChildren = menu.querySelectorAll('.header-mega-menu__wrapper > *, .header-mega-menu__collection-list > *, .mega-menu-custom > *');
              directChildren.forEach(child => {
                child.style.opacity = '0';
                child.style.transform = 'translateY(-20px)';
              });
            }, 200); // Match the transition duration
          };
          
          // Show menu on trigger hover
          menuTrigger.addEventListener('mouseenter', showMenu);
          
          // Hide menu with delay on trigger leave
          menuTrigger.addEventListener('mouseleave', () => {
            hideTimeout = setTimeout(hideMenu, 100);
          });
          
          // Keep menu visible when hovering over it
          menu.addEventListener('mouseenter', () => {
            if (hideTimeout) {
              clearTimeout(hideTimeout);
              hideTimeout = null;
            }
          });
          
          // Hide menu when leaving the menu itself
          menu.addEventListener('mouseleave', () => {
            hideTimeout = setTimeout(hideMenu, 100);
          });
        }
      });
    }
  },

  // Header dropdown slide-in and staggered animations
  initHeaderDropdownAnimations: function() {
    const dropdowns = document.querySelectorAll('.header__submenu');
    
    if (hasMotion) {
      dropdowns.forEach(dropdown => {
        // Set initial state for the dropdown container
        dropdown.style.opacity = '0';
        dropdown.style.transform = 'translateY(-10px)';
        
        // Add event listeners for hover states
        const dropdownTrigger = dropdown.previousElementSibling;
        if (dropdownTrigger) {
          let isDropdownVisible = false;
          let hideTimeout = null;
          
          const showDropdown = () => {
            if (hideTimeout) {
              clearTimeout(hideTimeout);
              hideTimeout = null;
            }
            
            if (isDropdownVisible) return;
            isDropdownVisible = true;
            
            // Slide in the dropdown
            motionLib.animate(dropdown, 
              { 
                opacity: [0, 1], 
                y: [-10, 0] 
              }, 
              { 
                duration: 0.25,
                easing: 'ease-out'
              }
            );
            
            // Stagger fadeInDown for direct children
            const directChildren = dropdown.querySelectorAll('li');
            directChildren.forEach((child, index) => {
              child.style.opacity = '0';
              child.style.transform = 'translateY(-8px)';
              
              motionLib.animate(child, 
                { 
                  opacity: [0, 1], 
                  y: [-8, 0] 
                }, 
                { 
                  duration: 0.3,
                  easing: 'ease-out',
                  delay: index * 0.05
                }
              );
            });
          };
          
          const hideDropdown = () => {
            if (!isDropdownVisible) return;
            isDropdownVisible = false;
            
            // Fade out the dropdown
            motionLib.animate(dropdown, 
              { 
                opacity: [1, 0], 
                y: [0, -10] 
              }, 
              { 
                duration: 0.2,
                easing: 'ease-in',
                onComplete: () => {
                  // Reset children only after dropdown is completely hidden
                  const directChildren = dropdown.querySelectorAll('li');
                  directChildren.forEach(child => {
                    child.style.opacity = '0';
                    child.style.transform = 'translateY(-8px)';
                  });
                }
              }
            );
          };
          
          // Show dropdown on trigger hover
          dropdownTrigger.addEventListener('mouseenter', showDropdown);
          
          // Hide dropdown with delay on trigger leave
          dropdownTrigger.addEventListener('mouseleave', () => {
            hideTimeout = setTimeout(hideDropdown, 300);
          });
          
          // Keep dropdown visible when hovering over it
          dropdown.addEventListener('mouseenter', () => {
            if (hideTimeout) {
              clearTimeout(hideTimeout);
              hideTimeout = null;
            }
          });
          
          // Hide dropdown when leaving it
          dropdown.addEventListener('mouseleave', () => {
            hideTimeout = setTimeout(hideDropdown, 300);
          });
        }
      });
    } else {
      // Fallback to CSS transitions
      dropdowns.forEach(dropdown => {
        dropdown.style.transition = 'opacity 0.25s ease-out, transform 0.25s ease-out';
        dropdown.style.opacity = '0';
        dropdown.style.transform = 'translateY(-10px)';
        
        const dropdownTrigger = dropdown.previousElementSibling;
        if (dropdownTrigger) {
          let isDropdownVisible = false;
          let hideTimeout = null;
          
          const showDropdown = () => {
            if (hideTimeout) {
              clearTimeout(hideTimeout);
              hideTimeout = null;
            }
            
            if (isDropdownVisible) return;
            isDropdownVisible = true;
            
            dropdown.style.opacity = '1';
            dropdown.style.transform = 'translateY(0)';
            
            // Stagger children with CSS
            const directChildren = dropdown.querySelectorAll('li');
            directChildren.forEach((child, index) => {
              child.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out';
              child.style.transitionDelay = `${index * 0.05}s`;
              child.style.opacity = '0';
              child.style.transform = 'translateY(-8px)';
              
              setTimeout(() => {
                child.style.opacity = '1';
                child.style.transform = 'translateY(0)';
              }, 50);
            });
          };
          
          const hideDropdown = () => {
            if (!isDropdownVisible) return;
            isDropdownVisible = false;
            
            dropdown.style.opacity = '0';
            dropdown.style.transform = 'translateY(-10px)';
            
            // Reset children after a delay
            setTimeout(() => {
              const directChildren = dropdown.querySelectorAll('li');
              directChildren.forEach(child => {
                child.style.opacity = '0';
                child.style.transform = 'translateY(-8px)';
              });
            }, 200);
          };
          
          // Show dropdown on trigger hover
          dropdownTrigger.addEventListener('mouseenter', showDropdown);
          
          // Hide dropdown with delay on trigger leave
          dropdownTrigger.addEventListener('mouseleave', () => {
            hideTimeout = setTimeout(hideDropdown, 300);
          });
          
          // Keep dropdown visible when hovering over it
          dropdown.addEventListener('mouseenter', () => {
            if (hideTimeout) {
              clearTimeout(hideTimeout);
              hideTimeout = null;
            }
          });
          
          // Hide dropdown when leaving it
          dropdown.addEventListener('mouseleave', () => {
            hideTimeout = setTimeout(hideDropdown, 300);
          });
        }
      });
    }
  },

  // Add hover zoom effects to mega menu images
  addMegaMenuHoverEffects: function(menu) {
    if (!hasMotion) return;
    
    // Collection list images - only if they're inside links
    const collectionImages = menu.querySelectorAll('.header-mega-menu__collection-list img');
    collectionImages.forEach(img => {
      const container = img.closest('li');
      const link = container ? container.querySelector('a') : null;
      
      
      // Only apply hover effects if there's a link
      if (container && link) {
        container.addEventListener('mouseenter', function() {
          motionLib.animate(img, 
            { 
              scale: [1, 1.12] 
            },
            { 
              duration: 0.3, 
              easing: 'ease-out' 
            }
          );
        });
        
        container.addEventListener('mouseleave', function() {
          motionLib.animate(img, 
            { 
              scale: [1.12, 1] 
            },
            { 
              duration: 0.3, 
              easing: 'ease-in' 
            }
          );
        });
      }
    });
    
    // Mega menu card images - only if they're inside links
    const cardImages = menu.querySelectorAll('.header-mega-menu__card img');
    cardImages.forEach(img => {
      const container = img.closest('.header-mega-menu__card');
      const link = container ? container.querySelector('a') : null;
      
      // Only apply hover effects if there's a link
      if (container && link) {
        container.addEventListener('mouseenter', function() {
          motionLib.animate(img, 
            { 
              scale: [1, 1.12] 
            },
            { 
              duration: 0.3, 
              easing: 'ease-out' 
            }
          );
        });
        
        container.addEventListener('mouseleave', function() {
          motionLib.animate(img, 
            { 
              scale: [1.12, 1] 
            },
            { 
              duration: 0.3, 
              easing: 'ease-in' 
            }
          );
        });
      }
    });
    
    // Custom mega menu images - only if they're inside links
    const customImages = menu.querySelectorAll('.mega-menu-custom img');
    customImages.forEach(img => {
      const container = img.closest('div');
      // For custom mega menu, check if there's a link in the same wrapper or if the image is clickable
      const link = container ? container.querySelector('a') : null;
      const isImageClickable = img.closest('a') !== null;
      
      
      // Only apply hover effects if there's a link in the container OR the image itself is a link
      if (container && (link || isImageClickable)) {
        container.addEventListener('mouseenter', function() {
          motionLib.animate(img, 
            { 
              scale: [1, 1.12] 
            },
            { 
              duration: 0.3, 
              easing: 'ease-out' 
            }
          );
        });
        
        container.addEventListener('mouseleave', function() {
          motionLib.animate(img, 
            { 
              scale: [1.12, 1] 
            },
            { 
              duration: 0.3, 
              easing: 'ease-in' 
            }
          );
        });
      }
    });
  },

  // Add hover zoom effects to mega menu images (CSS fallback)
  addMegaMenuHoverEffectsCSS: function(menu) {
    // Collection list images - only if they're inside links
    const collectionImages = menu.querySelectorAll('.header-mega-menu__collection-list img');
    collectionImages.forEach(img => {
      const container = img.closest('li');
      const link = container ? container.querySelector('a') : null;
      
      
      // Only apply hover effects if there's a link
      if (container && link) {
        img.style.transition = 'transform 0.3s ease';
        container.addEventListener('mouseenter', function() {
          img.style.transform = 'scale(1.12)';
        });
        container.addEventListener('mouseleave', function() {
          img.style.transform = 'scale(1)';
        });
      }
    });
    
    // Mega menu card images - only if they're inside links
    const cardImages = menu.querySelectorAll('.header-mega-menu__card img');
    cardImages.forEach(img => {
      const container = img.closest('.header-mega-menu__card');
      const link = container ? container.querySelector('a') : null;
      
      // Only apply hover effects if there's a link
      if (container && link) {
        img.style.transition = 'transform 0.3s ease';
        container.addEventListener('mouseenter', function() {
          img.style.transform = 'scale(1.12)';
        });
        container.addEventListener('mouseleave', function() {
          img.style.transform = 'scale(1)';
        });
      }
    });
    
    // Custom mega menu images - only if they're inside links
    const customImages = menu.querySelectorAll('.mega-menu-custom img');
    customImages.forEach(img => {
      const container = img.closest('div');
      // For custom mega menu, check if there's a link in the same wrapper or if the image is clickable
      const link = container ? container.querySelector('a') : null;
      const isImageClickable = img.closest('a') !== null;
      
      
      // Only apply hover effects if there's a link in the container OR the image itself is a link
      if (container && (link || isImageClickable)) {
        img.style.transition = 'transform 0.3s ease';
        container.addEventListener('mouseenter', function() {
          img.style.transform = 'scale(1.12)';
        });
        container.addEventListener('mouseleave', function() {
          img.style.transform = 'scale(1)';
        });
      }
    });
  },

  // Meter/progress animations
  initMeterAnimations: function() {
    const meters = document.querySelectorAll('#MainContent .meter-value');
    
    if (hasMotion) {
      meters.forEach((meter, index) => {
        motionLib.inView(meter, ({ target }) => {
          const meterValue = target.dataset.meterValue || '0';
          const finalValue = parseInt(meterValue);
          
          motionLib.animate(
            (progress) => {
              const currentValue = Math.round(progress * finalValue);
              target.textContent = currentValue;
            },
            { 
              duration: AnimationConfig.meters.duration,
              easing: AnimationConfig.meters.easing,
              delay: index * AnimationConfig.meters.staggerDelay
            }
          );
        }, {
          margin: AnimationConfig.meters.rootMargin,
          once: true
        });
      });
    } else {
      // Fallback to Intersection Observer
      const meterObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            const meter = entry.target;
            const meterValue = meter.dataset.meterValue || '0';
            const targetWidth = meterValue + '%';
            
            meter.style.transition = 'none';
            meter.style.width = '0%';
            meter.offsetHeight;
            
            setTimeout(() => {
              meter.style.transition = 'width 1s ease-out';
              meter.style.width = targetWidth;
            }, 1000 + (index * 200));
            
            meterObserver.unobserve(meter);
          }
        });
      }, {
        threshold: AnimationConfig.meters.threshold
      });

      meters.forEach(meter => {
        meterObserver.observe(meter);
      });
    }
  },

  // Lazy loading for product recommendations
  initLazyLoading: function() {
    const lazyElements = document.querySelectorAll('[data-lazy-load]');
    
    if (hasMotion) {
      lazyElements.forEach(element => {
        motionLib.inView(element, ({ target }) => {
          this.loadLazyContent(target);
        }, {
          margin: AnimationConfig.lazyLoad.rootMargin,
          once: true
        });
      });
    } else {
      const lazyObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadLazyContent(entry.target);
            lazyObserver.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: AnimationConfig.lazyLoad.rootMargin
      });

      lazyElements.forEach(element => {
        lazyObserver.observe(element);
      });
    }
  },

  // Load lazy content
  loadLazyContent: function(element) {
    const url = element.dataset.url;
    if (!url) return;
    
    fetch(url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const content =
          html.querySelector('product-recommendations') ||
          html.querySelector('product-recommendations--single');
        if (content && content.innerHTML.trim().length) {
          element.innerHTML = content.innerHTML;
        }
      })
      .catch(e => {
        console.error('Lazy loading error:', e);
      });
  },

  // Parallax effects (existing functionality)
  initParallaxEffects: function() {
    const parallaxElements = new Map();
    
    const initParallax = () => {
      document.querySelectorAll('[data-parallax]').forEach(element => {
        const rect = element.getBoundingClientRect();
        const speed = parseFloat(element.dataset.parallax) || 1;
        
        parallaxElements.set(element, {
          initialY: rect.top + window.pageYOffset,
          height: rect.height,
          speed: speed
        });
      });
    };
    
    initParallax();
    
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(initParallax, 100);
    });
    
    const updateParallax = () => {
      const scrolled = window.pageYOffset;
      const viewportHeight = window.innerHeight;
      
      parallaxElements.forEach((data, element) => {
        const { initialY, height, speed } = data;
        const elementTop = initialY - scrolled;
        const progress = -elementTop / viewportHeight;
        
        if (elementTop < viewportHeight + height && elementTop > -height) {
          const isMobile = window.innerWidth < 768;
          const parallaxSpeed = isMobile ? 30 : 100;
          const parallaxOffset = parallaxSpeed * (0.5 - progress);
          element.style.transform = `translateY(${parallaxOffset}px)`;
        }
      });
      
      requestAnimationFrame(updateParallax);
    };
    
    requestAnimationFrame(updateParallax);
  },

  // Responsive iframes (existing functionality)
  initResponsiveIframes: function() {
    const wrapIframe = (iframe) => {
      const width = iframe.width || iframe.getAttribute('width') || 16;
      const height = iframe.height || iframe.getAttribute('height') || 9;
      const ratio = (height / width) * 100;

      const wrapper = document.createElement('div');
      wrapper.className = 'responsive-iframe-wrapper';
      wrapper.style.position = 'relative';
      wrapper.style.width = '100%';
      wrapper.style.paddingTop = ratio + '%';

      iframe.style.position = 'absolute';
      iframe.style.top = '0';
      iframe.style.left = '0';
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.border = '0';

      iframe.parentNode.insertBefore(wrapper, iframe);
      wrapper.appendChild(iframe);
    };

    const blogContent = document.querySelector('.article-content__wrapper');
    if (blogContent) {
      const iframes = blogContent.querySelectorAll('iframe');
      iframes.forEach(wrapIframe);
    }
  },

  // Image loading states (existing functionality)
  initImageLoading: function() {
    const productImages = document.querySelectorAll('#MainContent .grid__item img');
    productImages.forEach(img => {
      const wrapper = img.parentElement;
      wrapper.classList.add('product-image-loading');
      
      img.addEventListener('load', () => {
        wrapper.classList.add('product-image-loaded');
      });

      if (img.complete) {
        wrapper.classList.add('product-image-loaded');
      }
    });
  },

  // Product media loading (existing functionality)
  initProductMedia: function() {
    const handleProductMedia = () => {
      const mediaList = document.querySelector('.product__media-list');
      if (!mediaList) return;

      mediaList.style.minHeight = '300px';

      const productDetailImages = mediaList.querySelectorAll('.product__media-item img, .product__media-item video');
      let loadedItems = 0;
      const totalItems = productDetailImages.length;

      productDetailImages.forEach(media => {
        const wrapper = media.closest('.product__media-item');
        if (!wrapper) return;
        
        wrapper.classList.add('media-loading');
        
        const markAsLoaded = () => {
          media.style.opacity = '1';
          wrapper.classList.add('media-loaded');
          loadedItems++;
          
          if (loadedItems === totalItems) {
            mediaList.classList.add('slider-initialized');
          }
        };
        
        if (media.tagName === 'IMG') {
          media.style.opacity = '0';
          media.style.transition = 'opacity 0.3s ease-out';
          
          const handleLoad = () => {
            setTimeout(markAsLoaded, 100);
          };

          media.addEventListener('load', handleLoad);
          if (media.complete) {
            handleLoad();
          }
        } else if (media.tagName === 'VIDEO') {
          media.style.opacity = '0';
          media.style.transition = 'opacity 0.3s ease-out';
          
          media.addEventListener('loadeddata', () => {
            setTimeout(markAsLoaded, 100);
          });
        }
      });
    };

    handleProductMedia();

    const handleProductSlider = () => {
      const productSwiper = document.querySelector('.product-swiper');
      if (!productSwiper) return;

      const slides = productSwiper.querySelectorAll('.swiper-slide');
      slides.forEach(slide => {
        const media = slide.querySelector('img, video');
        if (!media) return;

        if (media.tagName === 'IMG') {
          const handleLoad = () => {
            slide.classList.add('slide-loaded');
          };

          if (media.complete) {
            handleLoad();
          } else {
            media.addEventListener('load', handleLoad);
          }
        } else if (media.tagName === 'VIDEO') {
          media.addEventListener('loadeddata', () => {
            slide.classList.add('slide-loaded');
          });
        }
      });
    };

    handleProductSlider();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          handleProductSlider();
        }
      });
    });

    const productSwiper = document.querySelector('.product-swiper');
    if (productSwiper) {
      observer.observe(productSwiper, { childList: true, subtree: true });
    }
  },

  // Breakpoint container hover effects
  initBreakpointContainerHoverEffects: function() {
    const breakpointContainers = document.querySelectorAll('.breakpoint-container[style*="background-image"]');
    
    if (hasMotion) {
      breakpointContainers.forEach(container => {
        // Create a pseudo-element for the background image zoom effect
        const style = document.createElement('style');
        style.textContent = `
          .breakpoint-container[data-hover-enabled]::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-image: inherit;
            background-size: inherit;
            background-position: inherit;
            background-repeat: inherit;
            z-index: 0;
            transition: transform 0.3s ease;
          }
          .breakpoint-container[data-hover-enabled]:hover::before {
            transform: scale(1.1);
          }
        `;
        document.head.appendChild(style);
        
        // Mark container as having hover effects enabled
        container.setAttribute('data-hover-enabled', 'true');
      });
    } else {
      // CSS fallback for hover effects
      const style = document.createElement('style');
      style.textContent = `
        .breakpoint-container[data-hover-enabled]::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: inherit;
          background-size: inherit;
          background-position: inherit;
          background-repeat: inherit;
          z-index: 0;
          transition: transform 0.3s ease;
        }
        .breakpoint-container[data-hover-enabled]:hover::before {
          transform: scale(1.1);
        }
      `;
      document.head.appendChild(style);
      
      breakpointContainers.forEach(container => {
        container.setAttribute('data-hover-enabled', 'true');
      });
    }
  },

  // Card list: no stagger/fade-in-up (was jittery); cards show normally
  initCardListAnimations: function() {
    // Disabled: stagger fade-in-up on card list was jittery; cards now display without entrance animation
  },

  // Article list staggered entrance animations
  initArticleListAnimations: function() {
    const articleLists = document.querySelectorAll('.article-list-v2__grid');

    articleLists.forEach(articleList => {
      const cards = articleList.querySelectorAll('.article-card-v2');

      if (hasMotion) {
        cards.forEach((card, index) => {
          card.classList.add('animate-article-card');

          // Set initial state
          card.style.opacity = '0';
          card.style.transform = 'translateY(20px) scale(0.98)';

          motionLib.inView(card, ({ target }) => {
            motionLib.animate(target,
              {
                opacity: [0, 1],
                y: [20, 0],
                scale: [0.98, 1]
              },
              {
                duration: AnimationConfig.products.duration,
                easing: AnimationConfig.products.easing,
                delay: index * AnimationConfig.products.staggerDelay
              }
            );
          }, {
            threshold: AnimationConfig.products.threshold,
            rootMargin: AnimationConfig.products.rootMargin,
            once: true
          });
        });
      } else {
        // Fallback to Intersection Observer
        const cardObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry, index) => {
            if (entry.isIntersecting) {
              setTimeout(() => {
                entry.target.style.opacity = '1';
                entry.target.style.transform = 'translateY(0) scale(1)';
                entry.target.classList.add('animated');
              }, index * 50);
              cardObserver.unobserve(entry.target);
            }
          });
        }, {
          threshold: AnimationConfig.products.threshold,
          rootMargin: AnimationConfig.products.rootMargin
        });

        cards.forEach(card => {
          card.classList.add('animate-article-card');
          // Set initial state for fallback
          card.style.opacity = '0';
          card.style.transform = 'translateY(20px) scale(0.98)';
          card.style.transition = `opacity ${AnimationConfig.products.duration}s ${AnimationConfig.products.easing}, transform ${AnimationConfig.products.duration}s ${AnimationConfig.products.easing}`;
          cardObserver.observe(card);
        });
      }
    });
  },

  // Generic entrance animations for any element with the animate-in-on-scroll class
  initGenericEntranceAnimations: function() {
    // Target any element with the generic animation class
    const animatedElements = document.querySelectorAll('.animate-in-on-scroll');
    
    animatedElements.forEach((element, index) => {
      // Check if element is inside a Swiper - no stagger delay for sliders
      const isInSwiper = element.closest('.swiper-slide') !== null;
      const animationDelay = isInSwiper ? 0 : index * 0.1;
      
      if (hasMotion) {
        // Motion.js animation
        motionLib.inView(element, ({ target }) => {
          motionLib.animate(target, 
            { 
              opacity: [0, 1], 
              y: [20, 0],
              scale: [0.98, 1]
            }, 
            { 
              duration: 0.6,
              easing: 'ease-out',
              delay: animationDelay
            }
          );
        }, {
          threshold: 0.1,
          rootMargin: '50px 0px -50px 0px'
        });
      } else {
        // CSS fallback
        element.style.opacity = '0';
        element.style.transform = 'translateY(20px) scale(0.98)';
        element.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
        element.style.transitionDelay = `${animationDelay}s`;
        
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.style.opacity = '1';
              entry.target.style.transform = 'translateY(0) scale(1)';
              observer.unobserve(entry.target);
            }
          });
        }, {
          threshold: 0.1,
          rootMargin: '50px 0px -50px 0px'
        });
        
        observer.observe(element);
      }
    });
  },


  // Utility methods
  registerCustomAnimation: function(element, config) {
    if (hasMotion) {
      motionLib.inView(element, ({ target }) => {
        motionLib.animate(target, config.from, config.to);
      }, config.options || {});
    }
  },

  reinit: function() {
    this.init();
  }
};

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  window.AnimationManager.init();
});

// Re-initialize on section load (Shopify theme editor)
document.addEventListener('shopify:section:load', function() {
  setTimeout(() => {
    window.AnimationManager.reinit();
  }, 100);
}); 